/**
 * Sync/ETL framework contracts (KAN-45).
 *
 * KAN-34 requires that sync jobs "run on a schedule, retry on failure, and log
 * success/failure per run". `SyncRun` is that log: one row per attempt, with
 * the outcome and record counts, so a failed overnight sync is visible the
 * next morning rather than being inferred from missing data.
 */
export const SYNC_SOURCES = ['clio', 'qbo'];
/**
 * Sources whose records belong to one upstream *company* rather than to the
 * source as a whole.
 *
 * QuickBooks only, and it is a property of the firm rather than of the API:
 * Divorce with a Plan keeps two QBO companies — one holding the trust accounts,
 * one holding operating banking and payables — and a QBO record id is unique
 * within a company and nowhere else. Clio is one account for the firm, so a
 * Clio id identifies a record on its own.
 */
export const COMPANY_SCOPED_SOURCES = ['qbo'];
export function isCompanyScoped(source) {
    return COMPANY_SCOPED_SOURCES.includes(source);
}
/**
 * The `source_company` of a row from a source with no company dimension.
 *
 * Empty rather than null, and named rather than written as a bare `''`: it is
 * part of a unique key, and Postgres treats NULLs as distinct — a nullable
 * column would have stopped the key enforcing anything at all. Reading
 * `NO_COMPANY` at a call site says "this source has no companies", which a
 * literal does not.
 */
export const NO_COMPANY = '';
/**
 * The source a resource belongs to, read off its own name.
 *
 * Lives beside `SYNC_RESOURCES` because the `<source>.<collection>` shape is a
 * property of that list rather than of any one caller — a second copy of this
 * rule elsewhere is a second thing to update when a third source appears.
 */
export function sourceOfResource(resource) {
    return resource.startsWith('qbo.') ? 'qbo' : 'clio';
}
/**
 * A resource is one upstream collection. Splitting sync per-resource means a
 * failure pulling QBO bills does not roll back a successful invoice pull.
 */
export const SYNC_RESOURCES = [
    'clio.clients',
    'clio.attorneys',
    'clio.matters',
    'clio.time_entries',
    'clio.invoices',
    'clio.trust_transactions',
    'clio.payments',
    'clio.calendar_entries',
    'qbo.accounts',
    'qbo.bills',
    'qbo.vendors',
    /**
     * BillPayment — when a bill was settled. **Not** QuickBooks' customer
     * receipts: Clio is this platform's system of record for money in, and a
     * second copy of the same receipts would double collections. See
     * `syncBillPayments`.
     */
    'qbo.payments',
    /**
     * RefundReceipt — money returned to a client out of the **operating**
     * account. Refunds out of trust arrive from Clio instead, off the ledger
     * movement that evidences them, and the connector excludes them here so one
     * refund is not reported by both sources. See `syncRefunds`.
     */
    'qbo.refunds',
];
export const DEFAULT_RETRY_POLICY = {
    maxAttempts: 5,
    backoffMs: 5_000,
    strategy: 'exponential',
};
/**
 * How long a `running` run may go without a heartbeat before it is presented as
 * stalled rather than in progress.
 *
 * Generous on purpose: the heartbeat is throttled to a couple of seconds, so
 * anything past a few minutes means the worker is gone, not slow. Both the API
 * and the screen read this constant, so the two never disagree about what
 * "stalled" means.
 */
export const SYNC_STALL_AFTER_MS = 5 * 60_000;
//# sourceMappingURL=sync.js.map